import { supabase } from "@/integrations/supabase/client";

/** Single entry point for the admin security backend. */
export async function callSecurityAdmin(action: string, payload: Record<string, unknown> = {}) {
  const { data, error } = await supabase.functions.invoke("security-admin", {
    body: { action, ...payload },
  });
  if (error) throw new Error((data as any)?.error || error.message);
  if ((data as any)?.error) throw new Error((data as any).error);
  return data as any;
}

export interface StaffIdentity {
  name: string | null;
  email: string | null;
  designation: string | null;
  employee_code: string | null;
  staff_status: string | null;
}

export interface SignInRequest {
  id: string;
  user_id: string;
  ip_address: string | null;
  country: string | null;
  city: string | null;
  user_agent: string | null;
  device_label: string;
  status: string;
  grant_type: string | null;
  expires_at: string | null;
  review_note: string | null;
  reviewed_at: string | null;
  risk_score: number | null;
  risk_level: string | null;
  risk_signals: { key: string; weight: number; detail: string }[] | null;
  previous_country: string | null;
  previous_city: string | null;
  previous_seen_at: string | null;
  created_at: string;
  staff: StaffIdentity | null;
}

export interface StaffLocationRow {
  user_id: string;
  country: string | null;
  city: string | null;
  ip_address: string | null;
  device_label: string;
  last_seen_at: string;
  sign_ins: number;
  state: "recognised" | "new" | "suspicious" | "blocked";
  staff: StaffIdentity | null;
}

export interface LocationPolicy {
  id: string;
  user_id: string | null;
  country: string | null;
  city: string | null;
  ip_address: string | null;
  state: string;
  reason: string | null;
  created_at: string;
  staff: StaffIdentity | null;
}

export interface AdminSessionRow {
  id: string;
  user_id: string;
  ip_address: string | null;
  device_label: string;
  country: string | null;
  city: string | null;
  started_at: string;
  last_active_at: string;
  status: string;
  suspicious_reason: string | null;
  staff: StaffIdentity | null;
}

export const riskTone: Record<string, string> = {
  low: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  medium: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  high: "bg-orange-500/10 text-orange-500 border-orange-500/30",
  critical: "bg-destructive/10 text-destructive border-destructive/30",
};

export const locationStateTone: Record<string, string> = {
  recognised: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  new: "bg-sky-500/10 text-sky-500 border-sky-500/30",
  suspicious: "bg-amber-500/10 text-amber-500 border-amber-500/30",
  blocked: "bg-destructive/10 text-destructive border-destructive/30",
};

export function placeLabel(city?: string | null, country?: string | null) {
  return [city, country].filter(Boolean).join(", ") || "Unknown location";
}

export function staffLabel(staff: StaffIdentity | null, userId: string) {
  return staff?.name || staff?.email || `Staff ${userId.slice(0, 8)}`;
}
